// We are disabling deprecation rule in order to ensure the component still works for retrocompatibility
import { Children, type ReactNode } from 'react';

import { iterateXYChartAnnotations } from './iterate-xy-chart-annotations.js';
import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import type { ColorRuleProps } from '../../core/types/color-rule.js';
import { XYChartAnnotationsConfigProps } from '../context/XYChartAnnotationsConfig.context.js';
import { Annotations } from '../slots/Annotations/Annotations.js';
import { AreaSeries } from '../slots/AreaSeries.js';
import { BarSeries } from '../slots/BarSeries.js';
import { CustomTooltip } from '../slots/CustomTooltip.js';
import { DotSeries } from '../slots/DotSeries.js';
import { Legend } from '../slots/Legend.js';
import { LineSeries } from '../slots/LineSeries.js';
import { RectSeries } from '../slots/RectSeries.js';
import { Select } from '../slots/Select.js';
import { ThresholdIndicator } from '../slots/ThresholdIndicator.js';
import { Toolbar } from '../slots/Toolbar.js';
import { XYChartTooltipSlot } from '../slots/Tooltip.js';
import { XAxis } from '../slots/XAxis.js';
import { YAxis } from '../slots/YAxis.js';
import {
  AREA_VARIANT,
  BAR_VARIANT,
  DOT_VARIANT,
  LINE_VARIANT,
  RECT_VARIANT,
  type XYChartSlots,
} from '../types/xy-chart-internals.js';

/**
 * Extracts XY chart's slots
 */
export const iterateSlots = (children: ReactNode): XYChartSlots => {
  const slots: XYChartSlots & { colorRules: ColorRuleProps[] } = {
    series: [],
    yAxes: [],
    xAxes: [],
    legend: {},
    toolbar: {},
    thresholds: [],
    colorRules: [],
    tooltip: undefined,
  };

  let intents: IntentProps[] | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, RectSeries)) {
      slots.series.push({
        ...child.props,
        id: crypto.randomUUID(),
        type: RECT_VARIANT,
      });
    } else if (isSlot(child, DotSeries)) {
      slots.series.push({
        ...child.props,
        id: crypto.randomUUID(),
        type: DOT_VARIANT,
      });
    } else if (isSlot(child, LineSeries)) {
      slots.series.push({
        ...child.props,
        id: crypto.randomUUID(),
        type: LINE_VARIANT,
      });
    } else if (isSlot(child, BarSeries)) {
      slots.series.push({
        ...child.props,
        id: crypto.randomUUID(),
        type: BAR_VARIANT,
      });
    } else if (isSlot(child, AreaSeries)) {
      slots.series.push({
        ...child.props,
        id: crypto.randomUUID(),
        type: AREA_VARIANT,
      });
    } else if (isSlot(child, XAxis)) {
      slots.xAxes = [...slots.xAxes, child.props];
    } else if (isSlot(child, YAxis)) {
      slots.yAxes = [...slots.yAxes, child.props];
    } else if (isSlot(child, Legend)) {
      slots.legend = child.props;
    } else if (isSlot(child, Toolbar)) {
      slots.toolbar = child.props;
    } else if (isSlot(child, ThresholdIndicator)) {
      slots.thresholds.push(child.props);
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Select)) {
      const select = child.props;
      Object.assign(slots, { select });
    } else if (isSlot(child, Annotations)) {
      const annotations = iterateXYChartAnnotations(
        child.props as XYChartAnnotationsConfigProps,
      );
      if (annotations) {
        slots.annotations = annotations;
      }
    } else if (isSlot(child, ColorRule)) {
      isValidColorRule(child.props) && slots.colorRules.push(child.props);
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, CustomTooltip)) {
      slots.tooltip = {
        hidden: false,
        template: child.props.children,
        variant: 'single',
      };
    } else if (isSlot(child, XYChartTooltipSlot)) {
      slots.tooltip = {
        hidden: child.props.hidden ?? false,
        template: child.props.children,
        variant: child.props._variant ?? 'single',
        seriesDisplayMode: child.props._seriesDisplayMode,
      };
    }
  });

  return {
    ...slots,
    toolbar: {
      ...slots.toolbar,
      intents,
    },
  };
};
